import { useAtomValue } from 'jotai/index';
import { $CurrentArticle } from '@article/shared/atoms/current-article';
import { useMemo } from 'react';
import { ArticleDetailUtil, EArticleClass } from 'shared/models';
import { useTranslation } from 'react-i18next';
import { useArticleDescription } from './use-article-description';
import { unescapeSpecialXmlCharacter } from '~/utils/escape-xml';
import { $CurrentArticleRelatedHashtag } from '../atoms/current-article-related-hashtag';
import { $UseOptTitle } from '~/utils/libra/article-title-opt';
import { loadable } from 'jotai/utils';
import { $HitArticleGptTDK } from '~/utils/libra/article-gpt-tdk';
import { $CurrentArticleGPTInfo } from '../atoms/current-article-gpt-info';

export const useArticleTitle = (): string => {
  const loadableArticle = useAtomValue(loadable($CurrentArticle));
  const loadableRelatedHashtag = useAtomValue(loadable($CurrentArticleRelatedHashtag));
  const useOptTitle = useAtomValue($UseOptTitle);
  const { t } = useTranslation();
  const description = useArticleDescription();

  const hitArticleGptTDK = useAtomValue($HitArticleGptTDK);
  const gptInfo = useAtomValue($CurrentArticleGPTInfo);

  return useMemo(() => {
    if (loadableArticle.state !== 'hasData' || loadableRelatedHashtag.state !== 'hasData') {
      return '';
    }
    const article = loadableArticle.data;
    const { sugWords = [] } = loadableRelatedHashtag.data;

    const articleAvailable = ArticleDetailUtil.isArticleAvailable(article);

    if (!articleAvailable) {
      return `${t('article_not_found')} | Lemon8`;
    }

    if (hitArticleGptTDK && gptInfo?.title) {
      return gptInfo.title;
    }

    let optimizedMiddleTitle = '';

    let { title = '' } = article;

    // 2. if title not exists, use part of description as title.
    if (!title) {
      title = unescapeSpecialXmlCharacter(description.replace(/\s+/g, ' ')).slice(0, 30);
    }

    if (sugWords.length > 0 && sugWords[0].toLowerCase() !== title.toLowerCase()) {
      optimizedMiddleTitle = sugWords[0];
    }

    // 3. append suffix according to article class
    if (article.articleClass === EArticleClass.GALLERY) {
      title += ` | ${
        useOptTitle && optimizedMiddleTitle
          ? optimizedMiddleTitle
          : t('user_published_gallery').replace('%s', article.author.nickName)
      } | Lemon8`;
    } else if (article.articleClass === EArticleClass.VIDEO) {
      title += ` | ${
        useOptTitle && optimizedMiddleTitle
          ? optimizedMiddleTitle
          : t('own_video').replace('%s', article.author.nickName)
      } | Lemon8`;
    } else {
      title += ` | ${
        useOptTitle && optimizedMiddleTitle
          ? optimizedMiddleTitle
          : t('user_published_article').replace('%s', article.author.nickName)
      } | Lemon8`;
    }

    return title;
  }, [loadableArticle, loadableRelatedHashtag]);
};
